/**
 * OCPP connector status (spec 3.4) → the status set in `tokens.css`.
 *
 * Nine protocol states collapse onto five visual ones, and the collapse is the
 * design decision: an operator scanning a fleet table is asking "is anything
 * wrong, is anything working, is anything mid-transition". Giving `Preparing`,
 * `SuspendedEV`, `SuspendedEVSE` and `Finishing` four distinct colours would
 * make the table louder without answering that question any faster. The exact
 * protocol state is always printed as the label next to the mark, so nothing is
 * hidden — only de-emphasised.
 */

export type StatusTone = "available" | "charging" | "transitional" | "faulted" | "idle";

const TONES: Readonly<Record<string, StatusTone>> = {
  Available: "available",
  Charging: "charging",
  Preparing: "transitional",
  SuspendedEV: "transitional",
  SuspendedEVSE: "transitional",
  Finishing: "transitional",
  Reserved: "transitional",
  Faulted: "faulted",
  Unavailable: "idle",
};

/**
 * An unrecognised status is `idle`, not `faulted`. Vendors ship states outside
 * the enum, and colouring an unknown-but-harmless string red would train the
 * operator to ignore red.
 */
export function statusTone(status: string): StatusTone {
  return TONES[status] ?? "idle";
}

/**
 * `errorCode` is a separate field from `status` in OCPP: a connector can be
 * `Available` and simultaneously report a non-`NoError` code. That combination
 * is exactly the thing a fleet view exists to surface, so it is promoted to a
 * fault tone regardless of the status.
 */
export function connectorTone(status: string, errorCode: string): StatusTone {
  if (errorCode !== "" && errorCode !== "NoError") return "faulted";
  return statusTone(status);
}

/** Tailwind class fragments per tone, so no component writes a colour. */
export const TONE_TEXT: Readonly<Record<StatusTone, string>> = {
  available: "text-state-available",
  charging: "text-state-charging",
  transitional: "text-state-transitional",
  faulted: "text-state-faulted",
  idle: "text-state-idle",
};

export const TONE_BG: Readonly<Record<StatusTone, string>> = {
  available: "bg-state-available",
  charging: "bg-state-charging",
  transitional: "bg-state-transitional",
  faulted: "bg-state-faulted",
  idle: "bg-state-idle",
};

export const TONE_WASH: Readonly<Record<StatusTone, string>> = {
  available: "bg-state-available-wash text-state-available",
  charging: "bg-state-charging-wash text-state-charging",
  transitional: "bg-state-transitional-wash text-state-transitional",
  faulted: "bg-state-faulted-wash text-state-faulted",
  idle: "bg-state-idle-wash text-state-idle",
};
